import { Component, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmService, LayoutCanvasComponent, resizeImageBlob, blobToDataUrl } from '@luzula/luzula-lib';
import type { LayoutDoc, LayoutSummary, LayoutElement, LayoutElementType, LegendRow, ScaleShow } from '@luzula/luzula-lib';
import { ProjectService } from '../project.service';
import { LayoutsService } from '../layouts.service';
import { ProjectMarkersService } from '../project-markers.service';
import { punktartLegendRowsForTypes } from '../data-model';
import { MapSnapshotModalComponent, MapSnapshotResult } from './map-snapshot-modal.component';

export type PageOrientation = 'portrait' | 'landscape';

/** Eintrag der Werkzeugleiste (fuegt ein Element des Typs ein). */
interface Tool {
	id: LayoutElementType;
	label: string;
	icon: string;
}

/**
 * Druck-Layout-Designer (Schritt 2: Persistenz).
 *
 * Vollbild-Seite ueber `/druck-layout`. Zwei Ansichten:
 * - **Liste:** alle Layouts des aktiven Projekts (neu/oeffnen/umbenennen/loeschen),
 *   gespeichert in der dedizierten `bpweb-layouts`-DB ({@link LayoutsService}).
 * - **Editor:** die A4-Arbeitsflaeche eines Layouts (Hoch-/Querformat, Zoom,
 *   Speichern). Der eigentliche Canvas-Editor (`<wd-layout-canvas>` in luzula-lib),
 *   Karten-Snapshot, Massstab/Legende/Nordpfeil und der PNG/PDF-Export folgen in
 *   den naechsten Schritten — die Werkzeugleiste zeigt die geplanten Werkzeuge
 *   schon an, ist aber noch ohne Funktion.
 */
@Component({
	selector: 'bp-druck-layout',
	standalone: true,
	imports: [CommonModule, FormsModule, LayoutCanvasComponent],
	templateUrl: './druck-layout.component.html',
	styleUrls: ['./druck-layout.component.css'],
})
export class DruckLayoutComponent implements OnInit {
	private location = inject(Location);
	private layouts = inject(LayoutsService);
	private ps = inject(ProjectService);
	private confirm = inject(ConfirmService);
	private modal = inject(NgbModal);
	private projectMarkers = inject(ProjectMarkersService);

	/** DIN A4 in Millimetern. */
	static readonly A4_SHORT_MM = 210;
	static readonly A4_LONG_MM = 297;

	/** Basis-Aufloesung: 1 mm = PX_PER_MM_BASE px bei 100 % Zoom. */
	private static readonly PX_PER_MM_BASE = 3;

	/** Referenz auf den Canvas (für den PDF-Export aus dem Editor-Header). */
	@ViewChild('canvas') private canvasRef?: LayoutCanvasComponent;

	readonly view = signal<'list' | 'editor'>('list');
	readonly loading = signal(false);
	readonly saving = signal(false);
	readonly exporting = signal(false);
	readonly summaries = signal<LayoutSummary[]>([]);
	readonly current = signal<LayoutDoc | null>(null);
	readonly dirty = signal(false);

	/** Arbeitskopie der Elemente des offenen Layouts (Canvas-Datenfluss). */
	readonly editorElements = signal<LayoutElement[]>([]);
	readonly selectedId = signal<string | null>(null);
	readonly selectedElement = computed(() => this.editorElements().find((e) => e.id === this.selectedId()) ?? null);
	/** Elemente für die Auswahlliste, oberstes (höchstes z) zuerst. */
	readonly elementsByZ = computed(() => [...this.editorElements()].sort((a, b) => (b.z ?? 0) - (a.z ?? 0)));
	/** Magnetisches Einrasten (Snapping) an Raster/Kanten/Rändern. */
	readonly snapOn = signal(true);

	readonly projectId = signal<string | null>(null);
	readonly projectName = signal<string>('');

	readonly orientation = signal<PageOrientation>('portrait');
	/** Darstellungs-Zoom in Prozent (50–200). */
	readonly zoomPct = signal(100);

	readonly pageWidthMm = computed(() =>
		this.orientation() === 'portrait' ? DruckLayoutComponent.A4_SHORT_MM : DruckLayoutComponent.A4_LONG_MM,
	);
	readonly pageHeightMm = computed(() =>
		this.orientation() === 'portrait' ? DruckLayoutComponent.A4_LONG_MM : DruckLayoutComponent.A4_SHORT_MM,
	);
	readonly pageWidthPx = computed(() => this.mmToPx(this.pageWidthMm()));
	readonly pageHeightPx = computed(() => this.mmToPx(this.pageHeightMm()));

	/** Werkzeuge zum Einfügen von Elementen. */
	readonly tools: Tool[] = [
		{ id: 'map', label: 'Karte', icon: 'map' },
		{ id: 'text', label: 'Text', icon: 'fonts' },
		{ id: 'image', label: 'Bild', icon: 'image' },
		{ id: 'scale', label: 'Maßstab', icon: 'rulers' },
		{ id: 'legend', label: 'Legende', icon: 'list-ul' },
		{ id: 'north', label: 'Nordpfeil', icon: 'compass' },
		{ id: 'draw', label: 'Zeichnen', icon: 'pencil' },
	];

	async ngOnInit(): Promise<void> {
		const pid = this.ps.project() || null;
		this.projectId.set(pid);
		this.projectName.set((this.ps.currentProjectDoc() as { name?: string } | null)?.name ?? '');
		if (pid) await this.reload(pid);
	}

	// ---- Liste ----

	private async reload(pid: string): Promise<void> {
		this.loading.set(true);
		try {
			await this.layouts.openProject(pid);
			this.summaries.set(await this.layouts.listProject(pid));
		} finally {
			this.loading.set(false);
		}
	}

	private async refreshSummaries(): Promise<void> {
		const pid = this.projectId();
		if (pid) this.summaries.set(await this.layouts.listProject(pid));
	}

	async newLayout(): Promise<void> {
		const pid = this.projectId();
		if (!pid) return;
		const name = await this.confirm.prompt({
			title: 'Neues Druck-Layout',
			message: 'Wie soll das Layout heißen?',
			inputLabel: 'Name',
			inputPlaceholder: 'z. B. Übersichtskarte',
			inputRequired: true,
			confirmText: 'Anlegen',
		});
		if (!name?.trim()) return;
		const doc = await this.layouts.saveLayout({
			projectId: pid,
			name: name.trim(),
			page: { format: 'A4', orientation: this.orientation() },
			elements: [],
		});
		this.openDoc(doc);
		await this.refreshSummaries();
	}

	async open(layoutId: string): Promise<void> {
		const pid = this.projectId();
		if (!pid) return;
		const doc = await this.layouts.getLayout(pid, layoutId);
		if (doc) this.openDoc(doc);
	}

	async rename(s: LayoutSummary, ev?: Event): Promise<void> {
		ev?.stopPropagation();
		const pid = this.projectId();
		if (!pid) return;
		const name = await this.confirm.prompt({
			title: 'Layout umbenennen',
			message: 'Neuer Name:',
			inputLabel: 'Name',
			inputInitial: s.name,
			inputRequired: true,
			confirmText: 'Umbenennen',
		});
		if (!name?.trim() || name.trim() === s.name) return;
		const doc = await this.layouts.getLayout(pid, s.layoutId);
		if (!doc) return;
		await this.layouts.saveLayout({
			projectId: pid,
			layoutId: doc.layoutId,
			name: name.trim(),
			page: doc.page,
			elements: doc.elements,
			createdBy: doc.createdBy,
		});
		await this.refreshSummaries();
	}

	async remove(s: LayoutSummary, ev?: Event): Promise<void> {
		ev?.stopPropagation();
		const pid = this.projectId();
		if (!pid) return;
		const ok = await this.confirm.confirmDelete(`Layout „${s.name}“ wirklich löschen?`);
		if (!ok) return;
		await this.layouts.removeLayout(pid, s.layoutId);
		await this.refreshSummaries();
	}

	// ---- Editor ----

	private openDoc(doc: LayoutDoc): void {
		this.current.set(doc);
		this.editorElements.set(structuredClone(doc.elements ?? []));
		this.selectedId.set(null);
		this.orientation.set(doc.page.orientation);
		this.zoomPct.set(100);
		this.dirty.set(false);
		this.view.set('editor');
	}

	/** Canvas hat Elemente verändert (verschoben/skaliert/hinzugefügt/…). */
	onCanvasElements(els: LayoutElement[]): void {
		this.editorElements.set(els);
		this.dirty.set(true);
	}

	async save(): Promise<void> {
		const pid = this.projectId();
		const doc = this.current();
		if (!pid || !doc) return;
		this.saving.set(true);
		try {
			const updated = await this.layouts.saveLayout({
				projectId: pid,
				layoutId: doc.layoutId,
				name: doc.name,
				page: { format: 'A4', orientation: this.orientation() },
				elements: this.editorElements(),
				createdBy: doc.createdBy,
			});
			this.current.set(updated);
			this.dirty.set(false);
			await this.refreshSummaries();
		} finally {
			this.saving.set(false);
		}
	}

	async backToList(): Promise<void> {
		if (this.dirty()) {
			const discard = await this.confirm.confirmDiscard('Ungespeicherte Änderungen am Layout verwerfen?');
			if (!discard) return;
		}
		this.current.set(null);
		this.editorElements.set([]);
		this.selectedId.set(null);
		this.dirty.set(false);
		this.view.set('list');
	}

	setOrientation(o: PageOrientation): void {
		if (this.orientation() === o) return;
		this.orientation.set(o);
		if (this.view() === 'editor') this.dirty.set(true);
	}

	toggleOrientation(): void {
		this.setOrientation(this.orientation() === 'portrait' ? 'landscape' : 'portrait');
	}

	zoomIn(): void {
		this.zoomPct.update((z) => Math.min(200, z + 10));
	}

	zoomOut(): void {
		this.zoomPct.update((z) => Math.max(50, z - 10));
	}

	resetZoom(): void {
		this.zoomPct.set(100);
	}

	goBack(): void {
		this.location.back();
	}

	/** Icon-/Label-Nachschlag für das Eigenschaften-Panel (Single Source = tools). */
	iconForType(t: LayoutElementType): string {
		return this.tools.find((x) => x.id === t)?.icon ?? 'square';
	}
	labelForType(t: LayoutElementType): string {
		return this.tools.find((x) => x.id === t)?.label ?? t;
	}

	/** Numerische Geometrie-Eigenschaft des ausgewählten Elements aus einem Input-Feld setzen. */
	setProp(canvas: LayoutCanvasComponent, key: 'xMm' | 'yMm' | 'wMm' | 'hMm' | 'rotationDeg', ev: Event): void {
		const v = Number((ev.target as HTMLInputElement).value);
		if (!isNaN(v)) canvas.updateSelected({ [key]: v });
	}

	// ---- Typ-spezifische Eigenschaften (Text/Bild/Zeichnung) ----

	/** Eine typ-spezifische Eigenschaft (in element.props) des ausgewählten Elements setzen. */
	setProps(canvas: LayoutCanvasComponent, sel: LayoutElement, key: string, value: unknown): void {
		canvas.updateSelected({ props: { ...(sel.props ?? {}), [key]: value } });
	}

	strProp(sel: LayoutElement, key: string, def: string): string {
		const v = sel.props?.[key];
		return typeof v === 'string' ? v : def;
	}
	numProp(sel: LayoutElement, key: string, def: number): number {
		const v = sel.props?.[key];
		return typeof v === 'number' ? v : def;
	}
	boolProp(sel: LayoutElement, key: string): boolean {
		return !!sel.props?.[key];
	}

	/**
	 * Karten-Snapshot-Modal öffnen; Ergebnis (Bild + Maßstab/Position) ins Element schreiben.
	 * Mit `fromCurrentView` startet das Modal direkt auf der aktuellen Haupt-Karten-Ansicht
	 * und nimmt nach dem Laden automatisch auf (Schnell-Weg ohne manuelles Rahmen).
	 */
	async captureMap(
		canvas: LayoutCanvasComponent,
		sel: LayoutElement,
		opts: { fromCurrentView?: boolean } = {},
	): Promise<void> {
		// Bildschirmfüllend: viel größere Karte → Marker/Labels relativ kleiner, schärfer.
		const ref = this.modal.open(MapSnapshotModalComponent, { fullscreen: true, backdrop: 'static' });
		// Vorschau im selben Format wie das Karten-Element rahmen + Element-Größe für
		// maßstabsgenaues Rahmen (füllt das Element beim Aufnehmen).
		const modal = ref.componentInstance as MapSnapshotModalComponent;
		modal.targetAspect = sel.wMm > 0 && sel.hMm > 0 ? sel.wMm / sel.hMm : 4 / 3;
		modal.targetWidthMm = sel.wMm;
		modal.targetHeightMm = sel.hMm;
		if (opts.fromCurrentView) {
			modal.adoptOnOpen = true;
			modal.autoCapture = true;
		}
		const result = (await ref.result.catch(() => null)) as MapSnapshotResult | null;
		if (result?.src) canvas.updateSelected(this.mapElementPatch(sel, result));
	}

	/** Schnell-Weg: Karten-Element aus der aktuellen Haupt-Karten-Ansicht aufnehmen. */
	captureMapFromCurrentView(canvas: LayoutCanvasComponent, sel: LayoutElement): Promise<void> {
		return this.captureMap(canvas, sel, { fromCurrentView: true });
	}

	/**
	 * Baut das Element-Update aus dem Snapshot. Die Rahmengröße bleibt UNVERÄNDERT.
	 * Mit gewähltem Ziel-Maßstab wird dieser als fester `printScale` gespeichert; das
	 * Kartenbild wird im Rahmen maßstabsgetreu gezeigt (Überlauf beschnitten, Rest
	 * aufgefüllt). Ohne Ziel bleibt der Maßstab frei (folgt der Element-Breite).
	 */
	mapElementPatch(sel: LayoutElement, result: MapSnapshotResult): Partial<LayoutElement> {
		return {
			props: {
				...(sel.props ?? {}),
				src: result.src,
				center: result.center,
				zoom: result.zoom,
				groundWidthM: result.groundWidthM,
				groundHeightM: result.groundHeightM,
				printScale: result.targetScale ?? null,
			},
		};
	}

	/**
	 * Druckmaßstab eines Karten-Elements: fester `printScale`, wenn beim Aufnehmen
	 * gewählt; sonst aus Geländebreite ÷ aktueller Element-Breite abgeleitet (frei).
	 */
	mapScale(sel: LayoutElement): number {
		const fixed = this.numProp(sel, 'printScale', 0);
		if (fixed > 0) return fixed;
		const g = this.numProp(sel, 'groundWidthM', 0);
		return g > 0 && sel.wMm > 0 ? Math.round((g * 1000) / sel.wMm) : 0;
	}

	// ---- Maßstab / Legende / Nordpfeil (Schritt 6) ----

	/** Erstes Karten-Element mit aufgenommenem Snapshot — Quelle für Maßstab und Legende. */
	private firstMapElement(): LayoutElement | undefined {
		return this.editorElements().find((e) => e.type === 'map' && !!this.strProp(e, 'src', ''));
	}

	/** Gibt es eine aufgenommene Karte als Quelle? (Freigabe der „Aktualisieren"-Knöpfe). */
	hasMapSource(): boolean {
		return !!this.firstMapElement();
	}

	/** Punktart-Typen, deren Punkte im aufgenommenen Ausschnitt des Karten-Elements liegen. */
	private typesInMapExtent(mapEl: LayoutElement): string[] {
		const p = (mapEl.props ?? {}) as { center?: { lat: number; lng: number }; groundWidthM?: number; groundHeightM?: number };
		if (!p.center || !p.groundWidthM || !p.groundHeightM) return [];
		const { lat, lng } = p.center;
		const dLat = p.groundHeightM / 2 / 111320;
		const dLng = p.groundWidthM / 2 / (111320 * Math.cos((lat * Math.PI) / 180));
		const types: string[] = [];
		for (const m of this.projectMarkers.markers()) {
			const ll = m.latLng;
			if (!ll || !m.type) continue;
			if (ll.lat >= lat - dLat && ll.lat <= lat + dLat && ll.lng >= lng - dLng && ll.lng <= lng + dLng) {
				types.push(m.type);
			}
		}
		return types;
	}

	/**
	 * Aktueller Druckmaßstab-Nenner aus dem (ersten) Karten-Element. Nur für die
	 * Anzeige im Editor — der Maßstabsbalken leitet den Wert beim Rendern selbst
	 * live ab (folgt der Karte), daher kein „Übernehmen" mehr nötig.
	 */
	currentPrintScale(): number {
		const mapEl = this.firstMapElement();
		return mapEl ? this.mapScale(mapEl) : 0;
	}

	/** Darstellung der Maßstabsleiste (Balken/Text/Beides) umschalten. */
	setScaleShow(canvas: LayoutCanvasComponent, sel: LayoutElement, show: ScaleShow): void {
		this.setProps(canvas, sel, 'show', show);
	}

	/** Legende aus den im Karten-Ausschnitt vorkommenden Punktarten aktualisieren (eingefroren). */
	refreshLegend(canvas: LayoutCanvasComponent, sel: LayoutElement): void {
		const mapEl = this.firstMapElement();
		const types = mapEl ? this.typesInMapExtent(mapEl) : [];
		const rows: LegendRow[] = punktartLegendRowsForTypes(types).map((r) => ({
			nummer: r.nummer,
			beschreibung: r.beschreibung,
			svgUrl: r.invisible ? undefined : r.svgUrl,
			badgeLabel: r.invisible ? r.ohneSymbolLabel : undefined,
			badgeColor: r.invisible ? r.ohneSymbolColor : undefined,
		}));
		canvas.updateSelected({ props: { ...(sel.props ?? {}), rows } });
	}

	/** Anzahl Legenden-Zeilen des Elements (für den Hinweis im Panel). */
	legendRowCount(sel: LayoutElement): number {
		const rows = sel.props?.['rows'];
		return Array.isArray(rows) ? rows.length : 0;
	}

	// ---- PDF-Export (Schritt 7) ----

	/**
	 * html2canvas-Skalierung für eine Ziel-Auflösung (Standard ~200 dpi),
	 * unabhängig vom Anzeige-Zoom: Ziel-Pixelbreite ÷ aktuelle Element-Pixelbreite.
	 */
	pdfRenderScale(pageElWidthPx: number, dpi = 200): number {
		if (pageElWidthPx <= 0) return 1;
		const targetPx = (this.pageWidthMm() * dpi) / 25.4;
		return targetPx / pageElWidthPx;
	}

	/** Dateiname aus dem Layout-Namen säubern (für den Download). */
	pdfFileName(name: string): string {
		const safe = (name || 'layout').replace(/[^\w-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
		return `${safe || 'layout'}.pdf`;
	}

	/**
	 * Exportiert das Layout als A4-PDF. WYSIWYG: html2canvas fotografiert die
	 * Designer-Seite, die reinen Editor-Hilfen (Raster/Rahmen/Griffe/Hilfslinien)
	 * werden im geklonten DOM ausgeblendet (ohne die Live-Ansicht anzufassen).
	 * jsPDF/html2canvas werden erst hier geladen (Lazy-Import).
	 */
	async exportPdf(): Promise<void> {
		const canvas = this.canvasRef;
		const doc = this.current();
		if (!canvas || !doc || this.exporting()) return;
		const filename = this.pdfFileName(doc.name);

		// Speicherort ZUERST abfragen (solange die Nutzer-Geste frisch ist), bevor das
		// Rendern (mit await) startet — sonst verfällt die Geste für showSaveFilePicker.
		const picker = (window as { showSaveFilePicker?: (o: unknown) => Promise<any> }).showSaveFilePicker;
		let handle: any = null;
		if (typeof picker === 'function') {
			try {
				handle = await picker.call(window, {
					suggestedName: filename,
					types: [{ description: 'PDF-Dokument', accept: { 'application/pdf': ['.pdf'] } }],
				});
			} catch (e) {
				if ((e as DOMException)?.name === 'AbortError') return; // Nutzer hat abgebrochen
				handle = null; // Picker nicht nutzbar → normaler Download als Fallback
			}
		}

		this.exporting.set(true);
		try {
			this.selectedId.set(null); // Auswahl/Griffe aus der Aufnahme nehmen
			const page = canvas.getPageElement();
			const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
				import('html2canvas'),
				import('jspdf'),
			]);
			const rendered = await html2canvas(page, {
				scale: this.pdfRenderScale(page.offsetWidth),
				backgroundColor: '#ffffff',
				useCORS: true,
				onclone: (_doc: Document, el: HTMLElement) => this.stripEditorChrome(el),
			});
			const img = rendered.toDataURL('image/png');
			const pdf = new jsPDF({
				orientation: this.orientation() === 'landscape' ? 'landscape' : 'portrait',
				unit: 'mm',
				format: 'a4',
			});
			pdf.addImage(img, 'PNG', 0, 0, this.pageWidthMm(), this.pageHeightMm());
			const blob = pdf.output('blob') as Blob;
			if (handle) {
				const writable = await handle.createWritable();
				await writable.write(blob);
				await writable.close();
			} else {
				this.downloadBlob(blob, filename);
			}
		} catch (e) {
			console.error('[DruckLayout] PDF-Export fehlgeschlagen:', e);
			await this.confirm.alert('Der PDF-Export ist fehlgeschlagen.');
		} finally {
			this.exporting.set(false);
		}
	}

	/** Fallback-Download (Browser ohne File-System-Access-API): landet im Downloads-Ordner. */
	private downloadBlob(blob: Blob, filename: string): void {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}

	/** Blendet im geklonten DOM die reinen Editor-Hilfen aus (nur fürs Export-Bild). */
	private stripEditorChrome(page: HTMLElement): void {
		page.style.backgroundImage = 'none';
		page.style.boxShadow = 'none';
		page.querySelectorAll<HTMLElement>('.lc-el').forEach((e) => {
			e.style.border = 'none';
			e.style.background = 'transparent';
			e.style.boxShadow = 'none';
		});
		page.querySelectorAll('.lc-handle, .lc-rotate, .lc-guide').forEach((e) => e.remove());
	}

	/** Bild auswählen → verkleinern/komprimieren → als Data-URL in props.src ablegen. */
	async onImagePick(canvas: LayoutCanvasComponent, sel: LayoutElement, ev: Event): Promise<void> {
		const file = (ev.target as HTMLInputElement).files?.[0];
		if (!file) return;
		try {
			const blob = await resizeImageBlob(file, 1400, 0.82);
			const src = await blobToDataUrl(blob);
			this.setProps(canvas, sel, 'src', src);
		} catch (e) {
			console.error('[DruckLayout] Bild konnte nicht geladen werden:', e);
			await this.confirm.alert('Das Bild konnte nicht geladen werden.');
		}
	}

	/** Kurzformat fuer den „zuletzt geaendert"-Zeitstempel der Layout-Karten. */
	formatDate(iso?: string): string {
		if (!iso) return '';
		const d = new Date(iso);
		if (isNaN(d.getTime())) return '';
		return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
			+ ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
	}

	private mmToPx(mm: number): number {
		return Math.round(mm * DruckLayoutComponent.PX_PER_MM_BASE * (this.zoomPct() / 100));
	}
}
